/**
 * The shapes buildings are, as geometry.
 *
 * The town around a map used to be extruded boxes, and extruded boxes are why
 * a hundred maps looked like one map: a sand-coloured box and a grey box are
 * the same building. Cities are not made of boxes. They are made of podiums
 * with towers set back off them, drums with signage wrapped round them, sheds
 * with sawtooth roofs, temples with flared tiers, blocks whose corners are cut
 * or rounded because the street bends there.
 *
 * So this is a vocabulary rather than a shape. Each entry returns geometry in
 * a local frame — origin at the centre of its plot, base at y = 0, sized to the
 * plot it was given — and the scenery pass bakes it into one buffer with the
 * rest of the town. Everything here uses `Shape` and `ExtrudeGeometry` rather
 * than `BoxGeometry`, which is what buys the curves and the bevels: a rounded
 * corner is a `quadraticCurveTo`, and a chamfer is two `lineTo`s, and neither
 * costs anything the box was not already costing.
 *
 * None of it is collidable. The compiler owns every surface you can touch.
 */

import * as THREE from 'three';

/** A piece of a building: geometry in the plot's local frame, and its colour. */
export interface Piece {
  geo: THREE.BufferGeometry;
  colour: THREE.Color;
  /** True for pieces that should glow rather than take the light. */
  emissive?: boolean;
}

export interface Site {
  /** Plot size in metres. */
  w: number;
  d: number;
  /** How tall the crown may be, above the podium it sits on. */
  height: number;
  /** Height of the podium this sits on, in the plot's local frame. */
  base: number;
  wall: THREE.Color;
  trim: THREE.Color;
  accent: THREE.Color;
  glass: THREE.Color;
  rand: () => number;
}

// ---------------------------------------------------------------------------
// Footprints
// ---------------------------------------------------------------------------

function rect(w: number, d: number): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, -d / 2);
  s.lineTo(w / 2, -d / 2);
  s.lineTo(w / 2, d / 2);
  s.lineTo(-w / 2, d / 2);
  s.closePath();
  return s;
}

/** A rectangle with its corners cut off — the shape of a street corner block. */
function chamfered(w: number, d: number, c: number): THREE.Shape {
  const x = w / 2;
  const y = d / 2;
  const k = Math.min(c, x * 0.8, y * 0.8);
  const s = new THREE.Shape();
  s.moveTo(-x + k, -y);
  s.lineTo(x - k, -y);
  s.lineTo(x, -y + k);
  s.lineTo(x, y - k);
  s.lineTo(x - k, y);
  s.lineTo(-x + k, y);
  s.lineTo(-x, y - k);
  s.lineTo(-x, -y + k);
  s.closePath();
  return s;
}

/** A rectangle with rounded corners — the shape a curtain wall turns. */
function rounded(w: number, d: number, r: number): THREE.Shape {
  const x = w / 2;
  const y = d / 2;
  const k = Math.min(r, x * 0.9, y * 0.9);
  const s = new THREE.Shape();
  s.moveTo(-x + k, -y);
  s.lineTo(x - k, -y);
  s.quadraticCurveTo(x, -y, x, -y + k);
  s.lineTo(x, y - k);
  s.quadraticCurveTo(x, y, x - k, y);
  s.lineTo(-x + k, y);
  s.quadraticCurveTo(-x, y, -x, y - k);
  s.lineTo(-x, -y + k);
  s.quadraticCurveTo(-x, -y, -x + k, -y);
  s.closePath();
  return s;
}

function disc(r: number): THREE.Shape {
  const s = new THREE.Shape();
  s.absarc(0, 0, r, 0, Math.PI * 2, false);
  return s;
}

/**
 * Extrude a footprint upward, bevelled.
 *
 * ExtrudeGeometry builds along +z, so the result is rolled a quarter turn to
 * stand up. The bevel is small and always on: it is the difference between a
 * slab and a building with an edge that catches the light, and it is four
 * extra triangles per corner.
 */
function raise(shape: THREE.Shape, h: number, y: number, bevel = 0.22, segments = 10): THREE.BufferGeometry {
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(0.1, h - bevel * 2),
    bevelEnabled: bevel > 0,
    bevelSize: bevel,
    bevelThickness: bevel,
    bevelSegments: 1,
    curveSegments: segments,
  });
  g.rotateX(-Math.PI / 2);
  g.translate(0, y + bevel, 0);
  return g;
}

// ---------------------------------------------------------------------------
// Roof clutter — the thing that makes a roofline read as inhabited
// ---------------------------------------------------------------------------

function clutter(site: Site, top: number, spread: number, out: Piece[]): void {
  const { rand, wall, trim } = site;
  const n = 1 + Math.floor(rand() * 3);
  for (let i = 0; i < n; i++) {
    const kind = rand();
    const ox = (rand() - 0.5) * spread;
    const oz = (rand() - 0.5) * spread;
    if (kind < 0.4) {
      // A plant housing or lift overrun.
      const w = 1.6 + rand() * 2.4;
      const h = 1.4 + rand() * 2.2;
      const g = raise(chamfered(w, w * (0.7 + rand() * 0.8), 0.3), h, top, 0.12);
      g.translate(ox, 0, oz);
      out.push({ geo: g, colour: trim.clone().multiplyScalar(0.94) });
    } else if (kind < 0.72) {
      // A tank.
      const r = 0.7 + rand() * 1.2;
      const g = raise(disc(r), 1.4 + rand() * 2, top, 0.1, 12);
      g.translate(ox, 0, oz);
      out.push({ geo: g, colour: wall.clone().multiplyScalar(1.1) });
    } else {
      // An aerial mast.
      const g = raise(rect(0.24, 0.24), 3 + rand() * 5, top, 0);
      g.translate(ox, 0, oz);
      out.push({ geo: g, colour: trim.clone().multiplyScalar(0.6) });
    }
  }
}

// ---------------------------------------------------------------------------
// The vocabulary
// ---------------------------------------------------------------------------

/**
 * A neon palette built around the map's own accent.
 *
 * A street of signs all in one colour is a lighting rig, not a street. Rotating
 * the accent's hue by a few fixed steps keeps the map's identity — a red-signed
 * town stays red-signed — while giving each sign its own colour to be.
 */
function neon(accent: THREE.Color, rand: () => number): THREE.Color {
  /*
   * One hue, and it is the map's.
   *
   * This used to walk the accent's hue by up to half the wheel and return it
   * at 86% saturation, so a skyline carried five or six unrelated saturated
   * colours — which is five or six more than the world is allowed. A sign is
   * still a light source and still bright; it is simply the same light as
   * everything else on this map, at a lightness that varies and a hue that
   * does not.
   */
  const hsl = { h: 0, s: 0, l: 0 };
  accent.getHSL(hsl);
  return new THREE.Color().setHSL(hsl.h, 0.8, 0.5 + rand() * 0.14);
}

export type BlockKind =
  | 'setback'
  | 'tower'
  | 'drum'
  | 'chamfer'
  | 'curved'
  | 'pagoda'
  | 'sawtooth'
  | 'barrel'
  | 'signboard'
  | 'helipad'
  | 'gable'
  | 'arcade'
  | 'glassbox'
  | 'terrace';

type Builder = (s: Site) => Piece[];

const BLOCKS: Record<BlockKind, Builder> = {
  /** A stack that steps back as it rises — the classic city massing. */
  setback: (s) => {
    const out: Piece[] = [];
    const tiers = 2 + Math.floor(s.rand() * 3);
    let w = s.w;
    let d = s.d;
    let y = s.base;
    const each = s.height / tiers;
    for (let i = 0; i < tiers; i++) {
      const h = each * (0.7 + s.rand() * 0.6);
      out.push({ geo: raise(chamfered(w, d, Math.min(w, d) * 0.1), h, y), colour: s.wall.clone().multiplyScalar(1 - i * 0.04) });
      // A cornice slab marking the setback.
      out.push({ geo: raise(rect(w + 0.5, d + 0.5), 0.32, y + h - 0.32), colour: s.trim });
      y += h;
      w *= 0.7 + s.rand() * 0.14;
      d *= 0.7 + s.rand() * 0.14;
    }
    clutter(s, y, Math.min(w, d) * 0.6, out);
    return out;
  },

  /** A slim tower set off-centre on its podium. */
  tower: (s) => {
    const out: Piece[] = [];
    const w = s.w * (0.42 + s.rand() * 0.2);
    const d = s.d * (0.42 + s.rand() * 0.2);
    const ox = (s.w - w) * 0.5 * (s.rand() - 0.5) * 1.6;
    const oz = (s.d - d) * 0.5 * (s.rand() - 0.5) * 1.6;
    const h = s.height * (1.1 + s.rand() * 0.6);
    const g = raise(rounded(w, d, Math.min(w, d) * 0.22), h, s.base);
    g.translate(ox, 0, oz);
    out.push({ geo: g, colour: s.wall });
    const cap = raise(rect(w + 0.7, d + 0.7), 0.5, s.base + h - 0.5);
    cap.translate(ox, 0, oz);
    out.push({ geo: cap, colour: s.trim });
    clutter(s, s.base + h, Math.min(w, d) * 0.5, out);
    return out;
  },

  /** A cylindrical drum with a banded collar. */
  drum: (s) => {
    const out: Piece[] = [];
    const r = Math.min(s.w, s.d) * 0.46;
    const h = s.height * (0.9 + s.rand() * 0.5);
    out.push({ geo: raise(disc(r), h, s.base, 0.3, 20), colour: s.wall });
    const bands = 1 + Math.floor(s.rand() * 3);
    for (let i = 0; i < bands; i++) {
      const at = s.base + h * (0.25 + (i / bands) * 0.6);
      out.push({
        geo: raise(disc(r + 0.28), 0.9 + s.rand() * 0.9, at, 0.1, 20),
        colour: neon(s.accent, s.rand),
        emissive: true,
      });
    }
    out.push({ geo: raise(disc(r + 0.5), 0.4, s.base + h - 0.4, 0.12, 20), colour: s.trim });
    clutter(s, s.base + h, r, out);
    return out;
  },

  /** A block whose corners are cut, the way a street corner cuts them. */
  chamfer: (s) => {
    const out: Piece[] = [];
    const h = s.height * (0.8 + s.rand() * 0.5);
    out.push({ geo: raise(chamfered(s.w, s.d, Math.min(s.w, s.d) * 0.22), h, s.base), colour: s.wall });
    out.push({
      geo: raise(chamfered(s.w + 0.6, s.d + 0.6, Math.min(s.w, s.d) * 0.22), 0.42, s.base + h - 0.42),
      colour: s.trim,
    });
    clutter(s, s.base + h, Math.min(s.w, s.d) * 0.5, out);
    return out;
  },

  /** A curtain wall that turns the corner in a curve. */
  curved: (s) => {
    const out: Piece[] = [];
    const h = s.height * (0.9 + s.rand() * 0.6);
    const r = Math.min(s.w, s.d) * 0.4;
    out.push({ geo: raise(rounded(s.w, s.d, r), h, s.base, 0.18, 12), colour: s.wall });
    // Glazing bands wrapping the whole curve.
    const floors = Math.max(2, Math.floor(h / 3.4));
    for (let i = 1; i < floors; i++) {
      const at = s.base + (i / floors) * h;
      out.push({
        geo: raise(rounded(s.w + 0.16, s.d + 0.16, r), 1.15, at - 0.6, 0.06, 12),
        colour: s.glass,
        emissive: true,
      });
    }
    out.push({ geo: raise(rounded(s.w + 0.7, s.d + 0.7, r), 0.4, s.base + h - 0.4, 0.12, 12), colour: s.trim });
    clutter(s, s.base + h, r, out);
    return out;
  },

  /** Flared tiers on posts — a temple, or anything that wants to be one. */
  pagoda: (s) => {
    const out: Piece[] = [];
    const tiers = 2 + Math.floor(s.rand() * 3);
    let w = s.w * 0.78;
    let d = s.d * 0.78;
    let y = s.base;
    const each = s.height / tiers;
    for (let i = 0; i < tiers; i++) {
      out.push({ geo: raise(rect(w, d), each * 0.62, y, 0.1), colour: s.wall.clone().multiplyScalar(1.03) });
      // The eave: a wide thin slab overhanging the storey below it.
      const eave = raise(chamfered(w + 2.4, d + 2.4, 1.1), 0.5, y + each * 0.62, 0.3);
      out.push({ geo: eave, colour: s.accent });
      const under = raise(chamfered(w + 1.5, d + 1.5, 0.8), 0.36, y + each * 0.62 - 0.36, 0.2);
      out.push({ geo: under, colour: s.accent.clone().multiplyScalar(0.78) });
      y += each * 0.62 + 0.5;
      w *= 0.8;
      d *= 0.8;
    }
    // The finial.
    out.push({ geo: raise(disc(0.28), 2.4, y, 0.1, 8), colour: s.trim });
    return out;
  },

  /** A shed with a run of north-light roof monitors. */
  sawtooth: (s) => {
    const out: Piece[] = [];
    const h = Math.min(s.height, 7 + s.rand() * 4);
    out.push({ geo: raise(rect(s.w, s.d), h, s.base, 0.15), colour: s.wall });
    const teeth = Math.max(2, Math.round(s.w / 4));
    const tw = s.w / teeth;
    for (let i = 0; i < teeth; i++) {
      const x = -s.w / 2 + tw * (i + 0.5);
      const g = raise(rect(tw * 0.86, s.d * 0.94), 1.5 + s.rand() * 0.6, s.base + h, 0.1);
      g.translate(x, 0, 0);
      out.push({ geo: g, colour: s.trim });
      // The glazed face of each tooth, leaning back.
      const glass = raise(rect(tw * 0.7, 0.3), 1.5, s.base + h + 0.4, 0.05);
      glass.translate(x, 0, s.d * 0.2);
      out.push({ geo: glass, colour: s.glass, emissive: true });
    }
    return out;
  },

  /** A barrel-vaulted hall — a hangar, a market, a station shed. */
  barrel: (s) => {
    const out: Piece[] = [];
    const h = Math.min(s.height, 6 + s.rand() * 3);
    out.push({ geo: raise(rect(s.w, s.d), h, s.base, 0.15), colour: s.wall });
    // A half-round roof, built as a lathe so it is genuinely curved.
    const r = Math.min(s.w, s.d) / 2;
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i <= 10; i++) {
      const a = (i / 10) * Math.PI;
      pts.push(new THREE.Vector2(Math.cos(a) * r, Math.sin(a) * r * 0.55));
    }
    const along = s.w >= s.d ? s.w : s.d;
    const vault = new THREE.LatheGeometry(
      pts.map((p) => new THREE.Vector2(Math.max(0.01, Math.abs(p.x)), p.y)),
      2,
    );
    // A lathe of two segments is a flat sheet; extrude the arc instead.
    vault.dispose();
    const arc = new THREE.Shape();
    arc.moveTo(-r, 0);
    for (let i = 0; i <= 12; i++) {
      const a = Math.PI - (i / 12) * Math.PI;
      arc.lineTo(Math.cos(a) * r, Math.sin(a) * r * 0.62);
    }
    arc.closePath();
    const roof = new THREE.ExtrudeGeometry(arc, { depth: along, bevelEnabled: false, curveSegments: 12 });
    if (s.w >= s.d) {
      roof.rotateY(Math.PI / 2);
      roof.translate(-along / 2, s.base + h, 0);
    } else {
      roof.translate(0, s.base + h, -along / 2);
    }
    out.push({ geo: roof, colour: s.trim });
    return out;
  },

  /** A block wearing a sign taller than it is. */
  signboard: (s) => {
    const out: Piece[] = [];
    const h = s.height * (0.7 + s.rand() * 0.4);
    out.push({ geo: raise(chamfered(s.w, s.d, 0.9), h, s.base), colour: s.wall });
    const signs = 2 + Math.floor(s.rand() * 3);
    for (let i = 0; i < signs; i++) {
      const tall = s.rand() < 0.55;
      const sw = tall ? 1.1 + s.rand() * 0.8 : 3 + s.rand() * 3;
      const sh = tall ? 5 + s.rand() * 7 : 1.4 + s.rand() * 1.2;
      const at = s.base + h * (0.25 + s.rand() * 0.6) - sh / 2;
      const side = Math.floor(s.rand() * 4);
      const g = raise(rect(sw, 0.5), sh, at, 0.08);
      const off = (side < 2 ? s.d : s.w) / 2 + 0.3;
      if (side === 0) g.translate((s.rand() - 0.5) * s.w * 0.6, 0, off);
      else if (side === 1) g.translate((s.rand() - 0.5) * s.w * 0.6, 0, -off);
      else {
        g.rotateY(Math.PI / 2);
        g.translate(side === 2 ? off : -off, 0, (s.rand() - 0.5) * s.d * 0.6);
      }
      out.push({ geo: g, colour: neon(s.accent, s.rand), emissive: true });
    }
    // A crown of masts.
    clutter(s, s.base + h, Math.min(s.w, s.d) * 0.5, out);
    return out;
  },

  /** A low roof you could land on, and the plant that goes with it. */
  helipad: (s) => {
    const out: Piece[] = [];
    const h = Math.min(s.height, 5 + s.rand() * 5);
    out.push({ geo: raise(rect(s.w, s.d), h, s.base, 0.2), colour: s.wall });
    const r = Math.min(s.w, s.d) * 0.3;
    out.push({ geo: raise(disc(r), 0.24, s.base + h, 0.08, 18), colour: s.trim });
    out.push({ geo: raise(disc(r * 0.62), 0.12, s.base + h + 0.24, 0.05, 18), colour: s.accent });
    // A parapet all the way round, so the roof reads as a place.
    for (const [dx, dz, ww, dd] of [
      [0, s.d / 2 - 0.2, s.w, 0.4],
      [0, -s.d / 2 + 0.2, s.w, 0.4],
      [s.w / 2 - 0.2, 0, 0.4, s.d],
      [-s.w / 2 + 0.2, 0, 0.4, s.d],
    ] as const) {
      const g = raise(rect(ww, dd), 0.9, s.base + h, 0.06);
      g.translate(dx, 0, dz);
      out.push({ geo: g, colour: s.trim.clone().multiplyScalar(0.9) });
    }
    clutter(s, s.base + h, Math.min(s.w, s.d) * 0.5, out);
    return out;
  },

  /** A pitched-roof house, the plain building everything else is not. */
  gable: (s) => {
    const out: Piece[] = [];
    const h = Math.min(s.height, 4 + s.rand() * 5);
    out.push({ geo: raise(rect(s.w, s.d), h, s.base, 0.12), colour: s.wall });
    const along = s.w >= s.d ? s.w : s.d;
    const span = s.w >= s.d ? s.d : s.w;
    const rise = Math.min(3, span * 0.36);
    const tri = new THREE.Shape();
    tri.moveTo(-span / 2 - 0.35, 0);
    tri.lineTo(span / 2 + 0.35, 0);
    tri.lineTo(0, rise);
    tri.closePath();
    const roof = new THREE.ExtrudeGeometry(tri, { depth: along + 0.6, bevelEnabled: false });
    if (s.w >= s.d) {
      roof.rotateY(Math.PI / 2);
      roof.translate(-(along + 0.6) / 2, s.base + h, 0);
    } else {
      roof.translate(0, s.base + h, -(along + 0.6) / 2);
    }
    out.push({ geo: roof, colour: s.accent });
    return out;
  },

  /**
   * A wholly glazed volume in a pale frame.
   *
   * The Mirror's Edge building: one clean shape, one material, and the only
   * colour in it is a single band of something saturated. It works because
   * everything around it is restrained, so it wants to be used sparingly.
   */
  glassbox: (s) => {
    const out: Piece[] = [];
    const h = s.height * (0.9 + s.rand() * 0.7);
    const r = Math.min(s.w, s.d) * (s.rand() < 0.5 ? 0.06 : 0.3);
    out.push({ geo: raise(rounded(s.w, s.d, r), h, s.base, 0.14, 12), colour: s.glass, emissive: true });
    // The frame: a slab at every floor, white and thin.
    const floors = Math.max(2, Math.floor(h / 3.6));
    for (let i = 0; i <= floors; i++) {
      const at = s.base + (i / floors) * (h - 0.3);
      out.push({
        geo: raise(rounded(s.w + 0.45, s.d + 0.45, r), 0.3, at, 0.08, 12),
        colour: s.trim.clone().multiplyScalar(1.25),
      });
    }
    // One band of colour, and nothing else.
    const at = s.base + h * (0.3 + s.rand() * 0.4);
    out.push({
      geo: raise(rounded(s.w + 0.6, s.d + 0.6, r), 0.7, at, 0.08, 12),
      colour: neon(s.accent, s.rand),
      emissive: true,
    });
    clutter(s, s.base + h, Math.min(s.w, s.d) * 0.4, out);
    return out;
  },

  /** A stepped roof terrace with parapets — somewhere you could run. */
  terrace: (s) => {
    const out: Piece[] = [];
    const steps = 2 + Math.floor(s.rand() * 2);
    let w = s.w;
    let d = s.d;
    let y = s.base;
    for (let i = 0; i < steps; i++) {
      const h = (s.height / steps) * (0.6 + s.rand() * 0.5);
      out.push({ geo: raise(rounded(w, d, Math.min(w, d) * 0.12), h, y, 0.16, 8), colour: s.wall.clone().multiplyScalar(1.06) });
      // The parapet round the deck this step leaves exposed.
      const ring = raise(rounded(w, d, Math.min(w, d) * 0.12), 1.0, y + h, 0.06, 8);
      const inner = raise(rounded(w - 0.7, d - 0.7, Math.min(w, d) * 0.12), 1.2, y + h - 0.1, 0.06, 8);
      out.push({ geo: ring, colour: s.trim.clone().multiplyScalar(1.15) });
      out.push({ geo: inner, colour: s.wall.clone().multiplyScalar(1.12) });
      // A stripe of colour along the edge, the way a running route is marked.
      out.push({
        geo: raise(rounded(w + 0.24, d + 0.24, Math.min(w, d) * 0.12), 0.26, y + h + 0.7, 0.05, 8),
        colour: s.accent,
        emissive: true,
      });
      y += h;
      w *= 0.78;
      d *= 0.78;
    }
    clutter(s, y, Math.min(w, d) * 0.5, out);
    return out;
  },

  /** A colonnade at street level with a solid block over it. */
  arcade: (s) => {
    const out: Piece[] = [];
    const openH = 3.6;
    const h = s.height * (0.8 + s.rand() * 0.5);
    const cols = Math.max(3, Math.round(s.w / 2.6));
    for (let i = 0; i < cols; i++) {
      const x = -s.w / 2 + (s.w / (cols - 1)) * i;
      for (const z of [-s.d / 2 + 0.4, s.d / 2 - 0.4]) {
        const g = raise(disc(0.42), openH, s.base, 0.08, 10);
        g.translate(x, 0, z);
        out.push({ geo: g, colour: s.trim });
      }
    }
    // The block the colonnade carries.
    out.push({ geo: raise(chamfered(s.w, s.d, 0.7), h, s.base + openH), colour: s.wall });
    out.push({ geo: raise(rect(s.w + 0.6, s.d + 0.6), 0.4, s.base + openH - 0.4), colour: s.trim });
    clutter(s, s.base + openH + h, Math.min(s.w, s.d) * 0.5, out);
    return out;
  },
};

export const BLOCK_KINDS = Object.keys(BLOCKS) as BlockKind[];

/** Build one plot's crown, or nothing if the kind is unknown. */
export function buildBlock(kind: BlockKind, site: Site): Piece[] {
  const make = BLOCKS[kind];
  return make ? make(site) : [];
}
